import { useEffect, useState, type FormEvent } from 'react';

import {
  CHECK_RESULTS,
  SNAG_SEVERITIES,
  listTemplates,
  type CheckResult,
  type ChecklistTemplate,
  type Inspection,
  type Ncr,
  type NcrStatus,
  type ProjectMember,
  type Severity,
} from '../api.js';
import { Attachments } from './Attachments.js';
import {
  MIN_REASON,
  MIN_TEXT,
  NCR_MOVES,
  RESULT_LABELS,
  SEVERITY_LABELS,
} from './quality-labels.js';

/**
 * The forms behind the quality register: walking a checklist, requesting an
 * inspection, finishing the walk, raising an NCR and moving one on.
 *
 * They live beside `Quality.tsx` rather than inside it because the register
 * and its five forms had grown into one 1,100-line file. Nothing about them
 * changed in the move — in particular:
 *
 *   **`ConcludeForm` still cannot say what the result was.** It shows the
 *   verdict the checklist already produced, offers no way to change it, and
 *   demands an NCR before it will let go of a failure.
 *
 * `EditForm` was added later, and late: the API had accepted an inspector, a
 * date, notes and a revised checklist since the register was built, and no
 * screen had ever called it. Every inspection the product could produce read
 * "No inspector named", and the request form's promise that lines can be
 * changed until the walk starts was one nothing could keep.
 */
// ---------------------------------------------------------------------------

interface ChecklistProps {
  readonly inspection: Inspection;
  readonly projectId: string;
  readonly busy: boolean;
  readonly editable: boolean;
  readonly onAnswer: (lineId: string, result: CheckResult | null, note: string | null) => void;
}

export function Checklist({
  inspection,
  projectId,
  busy,
  editable,
  onAnswer,
}: ChecklistProps): JSX.Element {
  return (
    <div className="checklist">
      {inspection.lines.map((line) => (
        <div className="checklist-line" key={line.id}>
          <span className="checklist-text">{line.text}</span>
          {editable ? (
            <div className="checklist-results" role="group" aria-label={line.text}>
              {CHECK_RESULTS.map((result) => (
                <button
                  key={result}
                  type="button"
                  className={`checklist-choice${line.result === result ? ' chosen' : ''}${
                    result === 'fail' ? ' fail' : ''
                  }`}
                  disabled={busy}
                  aria-pressed={line.result === result}
                  onClick={() => onAnswer(line.id, line.result === result ? null : result, line.note)}
                >
                  {RESULT_LABELS[result]}
                </button>
              ))}
            </div>
          ) : (
            <span className={`checklist-answer${line.result === 'fail' ? ' task-overdue' : ''}`}>
              {line.result ? RESULT_LABELS[line.result] : 'Not answered'}
            </span>
          )}
          {line.note && <span className="quality-note">{line.note}</span>}
          {/*
            A photo against the LINE, not the inspection.
            "Item 7 failed" and a folder of twenty photos is a puzzle; the
            photo belongs to the line it is evidence for. Only offered where
            there is something to show — an unanswered line has nothing yet.
          */}
          {line.result && (
            <Attachments
              projectId={projectId}
              entityType="checklist_response"
              entityId={line.id}
              can={{ write: editable }}
              noun="a photo"
            />
          )}
        </div>
      ))}
      {editable && (
        <p className="hint">
          A line marked Fail fails the whole inspection. That is not something anybody can
          override, so mark it as you find it.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface RequestProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    title: string;
    requested_for_date: string | null;
    lines: readonly string[];
  }) => void | Promise<void>;
}

export function RequestForm({ busy, onCancel, onSubmit }: RequestProps): JSX.Element {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [lines, setLines] = useState('');
  /**
   * The company's saved ITPs (Stage 8). Prefill ONLY — the template's lines
   * are copied into the textarea for the person to trim to today's walk. A
   * role without `template:view` simply gets no dropdown.
   */
  const [templates, setTemplates] = useState<readonly ChecklistTemplate[]>([]);

  useEffect(() => {
    void listTemplates()
      .then((view) => setTemplates(view.checklist.filter((t) => t.is_active)))
      .catch(() => undefined);
  }, []);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!title.trim()) return;
    void onSubmit({
      title: title.trim(),
      requested_for_date: date || null,
      lines: lines
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="ins-title">What is being inspected?</label>
          <input
            id="ins-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="Level 3 fire stopping"
          />
        </div>
        <div className="field">
          <label htmlFor="ins-date">Wanted by</label>
          <input
            id="ins-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      {templates.length > 0 && (
        <div className="field">
          <label htmlFor="ins-template">Start from a saved checklist</label>
          <select
            id="ins-template"
            defaultValue=""
            disabled={busy}
            onChange={(e) => {
              const template = templates.find((t) => t.id === e.target.value);
              if (template) setLines(template.items.map((i) => i.text).join('\n'));
            }}
          >
            <option value="">Type it fresh</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.items.length} lines)
              </option>
            ))}
          </select>
          <p className="hint">
            Copies the lines below for you to trim — the saved checklist itself never changes.
          </p>
        </div>
      )}

      <div className="field">
        <label htmlFor="ins-lines">The checklist, one line each</label>
        <textarea
          id="ins-lines"
          value={lines}
          onChange={(e) => setLines(e.target.value)}
          rows={5}
          disabled={busy}
          placeholder={'Penetrations sealed to specification\nCollars fitted where required\nLabelling present and legible'}
        />
        <p className="hint">
          Each line gets a Pass, Fail or N/A when it is walked. Lines can be added or changed
          right up until the walk starts, and not afterwards.
        </p>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !title.trim()}>
          {busy ? 'Requesting…' : 'Request it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface EditProps {
  readonly inspection: Inspection;
  readonly people: readonly ProjectMember[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    inspector_id: string | null;
    scheduled_for: string | null;
    notes: string | null;
    lines?: readonly string[];
  }) => void | Promise<void>;
}

/**
 * Naming the inspector, setting the date, and correcting the checklist.
 *
 * **The checklist disappears once the walk has started**, and the screen says
 * why rather than showing a disabled box. Lines are replaced wholesale, so
 * editing them halfway through would throw away the answers already given —
 * the API refuses it too, and this is the same rule said in advance instead of
 * after the fact.
 */
export function EditForm({ inspection, people, busy, onCancel, onSubmit }: EditProps): JSX.Element {
  const [inspector, setInspector] = useState(inspection.inspector_id ?? '');
  // `scheduled_at` is a timestamp and the input wants a date, so the day is
  // taken off the front rather than parsed — see `formatDate` for why parsing
  // a date out of a string is how a day moves by one in a timezone behind UTC.
  const [date, setDate] = useState((inspection.scheduled_at ?? '').slice(0, 10));
  const [notes, setNotes] = useState(inspection.notes ?? '');
  const [lines, setLines] = useState(inspection.lines.map((l) => l.text).join('\n'));

  const walking = inspection.status === 'in_progress';

  function submit(event: FormEvent): void {
    event.preventDefault();
    const trimmed = lines
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    void onSubmit({
      inspector_id: inspector || null,
      scheduled_for: date || null,
      notes: notes.trim() || null,
      // Omitted entirely once the walk is on, so a stale textarea cannot be
      // posted back over somebody's answers.
      ...(walking ? {} : { lines: trimmed }),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Edit {inspection.number}</p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="ins-edit-inspector">Who is walking it?</label>
          <select
            id="ins-edit-inspector"
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            disabled={busy}
          >
            <option value="">Nobody named yet</option>
            {people.map((person) => (
              <option key={person.user_id} value={person.user_id}>
                {person.name}
              </option>
            ))}
          </select>
          <p className="hint">
            Left empty, whoever starts the walk is recorded as the inspector. A quality record
            nobody signed is not one anybody can rely on.
          </p>
        </div>
        <div className="field">
          <label htmlFor="ins-edit-date">Scheduled for</label>
          <input
            id="ins-edit-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="ins-edit-notes">Notes (optional)</label>
        <textarea
          id="ins-edit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={busy}
        />
      </div>

      {walking ? (
        <p className="hint">
          <strong>The checklist is fixed now the walk has started.</strong> Changing the lines
          would throw away the answers already given against them.
        </p>
      ) : (
        <div className="field">
          <label htmlFor="ins-edit-lines">The checklist, one line each</label>
          <textarea
            id="ins-edit-lines"
            value={lines}
            onChange={(e) => setLines(e.target.value)}
            rows={5}
            disabled={busy}
          />
          <p className="hint">
            Replaces the whole checklist. This is the last point at which it can change — once
            the walk starts, it is fixed.
          </p>
        </div>
      )}

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface ConcludeProps {
  readonly inspection: Inspection;
  readonly people: readonly ProjectMember[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    observations?: string | null;
    ncr?: { description: string; severity: Severity; assignee_id: string | null };
  }) => void | Promise<void>;
}

/**
 * Finishing the walk.
 *
 * The form shows the result the checklist has already decided, and offers no
 * way to change it. When that result is a failure it demands an NCR before the
 * button will do anything.
 */
export function ConcludeForm({ inspection, people, busy, onCancel, onSubmit }: ConcludeProps): JSX.Element {
  const [observations, setObservations] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<Severity>('high');
  const [assignee, setAssignee] = useState('');

  const failing = inspection.failed_lines > 0;
  const blocked = inspection.unanswered > 0;
  const ready = !blocked && (!failing || description.trim().length >= MIN_TEXT);

  return (
    <div className="panel">
      <p className="panel-title">Finish the walk — {inspection.number}</p>

      {blocked ? (
        <div className="alert" role="alert">
          <span>
            {inspection.unanswered}{' '}
            {inspection.unanswered === 1 ? 'line is' : 'lines are'} still unanswered. An
            inspection with a blank line has not been walked.
          </span>
        </div>
      ) : (
        <p className={failing ? 'diary-verdict task-overdue' : 'diary-verdict'}>
          <strong>
            {failing
              ? `This inspection FAILS — ${inspection.failed_lines} ${
                  inspection.failed_lines === 1 ? 'line' : 'lines'
                } marked Fail`
              : observations.trim()
                ? 'This inspection passes, with observations'
                : 'This inspection passes'}
          </strong>
        </p>
      )}

      {failing && !blocked && (
        <p className="hint">
          A failed line fails the inspection, and that cannot be overridden. A failure also has to
          have an NCR raised against it — a failure with nothing raised is one nobody has to
          answer for.
        </p>
      )}

      {!failing && (
        <div className="field">
          <label htmlFor="ins-observations">Observations (optional)</label>
          <textarea
            id="ins-observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={2}
            maxLength={4000}
            disabled={busy}
            placeholder="Labelling is legible but faded on two collars."
          />
          <p className="hint">Anything noted here records the result as “passed with observations”.</p>
        </div>
      )}

      {failing && (
        <>
          <div className="field">
            <label htmlFor="ncr-description">What is the non-conformance?</label>
            <textarea
              id="ncr-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={4000}
              disabled={busy}
              placeholder="Fire stopping omitted at three penetrations on the level 3 riser."
            />
            <p className="hint">
              {description.trim().length}/{MIN_TEXT} characters minimum.
            </p>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="ncr-severity">How serious?</label>
              <select
                id="ncr-severity"
                value={severity}
                disabled={busy}
                onChange={(e) => setSeverity(e.target.value as Severity)}
              >
                {SNAG_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABELS[s]}
                  </option>
                ))}
              </select>
              <p className="hint">An open High or Critical NCR blocks the QA/QC stage.</p>
            </div>
            <div className="field">
              <label htmlFor="ncr-assignee">Who is putting it right?</label>
              <select
                id="ncr-assignee"
                value={assignee}
                disabled={busy}
                onChange={(e) => setAssignee(e.target.value)}
              >
                <option value="">Decide later</option>
                {people.map((p) => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !ready}
          onClick={() =>
            void onSubmit(
              failing
                ? {
                    ncr: {
                      description: description.trim(),
                      severity,
                      assignee_id: assignee || null,
                    },
                  }
                : { observations: observations.trim() || null },
            )
          }
        >
          {busy ? 'Recording…' : failing ? 'Record the failure and raise the NCR' : 'Record the result'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface NcrFormProps {
  readonly people: readonly ProjectMember[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    description: string;
    severity: Severity;
    assignee_id: string | null;
  }) => void | Promise<void>;
}

export function NcrForm({ people, busy, onCancel, onSubmit }: NcrFormProps): JSX.Element {
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [assignee, setAssignee] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (description.trim().length < MIN_TEXT) return;
    void onSubmit({
      description: description.trim(),
      severity,
      assignee_id: assignee || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Raise an NCR</p>
      <div className="field">
        <label htmlFor="ncr-new-description">What is the non-conformance?</label>
        <textarea
          id="ncr-new-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={4000}
          disabled={busy}
          placeholder="Fire stopping omitted at three penetrations on the level 3 riser."
        />
        <p className="hint">
          {description.trim().length}/{MIN_TEXT} characters minimum.
        </p>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="ncr-new-severity">How serious?</label>
          <select
            id="ncr-new-severity"
            value={severity}
            disabled={busy}
            onChange={(e) => setSeverity(e.target.value as Severity)}
          >
            {SNAG_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="ncr-new-assignee">Who is putting it right?</label>
          <select
            id="ncr-new-assignee"
            value={assignee}
            disabled={busy}
            onChange={(e) => setAssignee(e.target.value)}
          >
            <option value="">Decide later</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="button-row">
        <button
          type="submit"
          className="inline"
          disabled={busy || description.trim().length < MIN_TEXT}
        >
          {busy ? 'Raising…' : 'Raise it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface NcrMoveProps {
  readonly ncr: Ncr;
  readonly to: NcrStatus;
  readonly people: readonly ProjectMember[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    reason?: string;
    corrective_action?: string;
    assignee_id?: string | null;
    due_date?: string | null;
    completion_note?: string;
  }) => void | Promise<void>;
}

export function NcrMoveForm({ ncr, to, people, busy, onCancel, onSubmit }: NcrMoveProps): JSX.Element {
  const [text, setText] = useState('');
  const [assignee, setAssignee] = useState(ncr.assignee_id ?? '');
  const [due, setDue] = useState(ncr.due_date ?? '');

  const isAssign = to === 'corrective_action_assigned' && ncr.status === 'open';
  const isSendBack = to === 'corrective_action_assigned' && ncr.status === 'work_complete';
  const isDone = to === 'work_complete';
  const isVoid = to === 'void';

  const minimum = isVoid || isSendBack ? MIN_REASON : MIN_TEXT;
  const ready = text.trim().length >= minimum && (!isAssign || Boolean(assignee));

  return (
    <div className="panel">
      <p className="panel-title">
        {NCR_MOVES[to]} — {ncr.number}
      </p>
      <p className="hint">
        {isAssign
          ? 'An NCR needs a corrective action and somebody responsible for it. Without both it is a complaint with a name attached.'
          : isSendBack
            ? 'Say what is still wrong. It goes on the record and the person putting it right sees it.'
            : isDone
              ? 'Say what was done. Somebody else has to check it — you cannot check your own work. (This becomes an evidence-document requirement when documents exist.)'
              : 'A voided NCR keeps its number. The record has to say why it was dropped.'}
      </p>

      <div className="field">
        <label htmlFor="ncr-move-text">
          {isAssign
            ? 'What has to be done?'
            : isDone
              ? 'What was done?'
              : isSendBack
                ? 'What is still wrong?'
                : 'Why?'}
        </label>
        <textarea
          id="ncr-move-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={4000}
          disabled={busy}
          placeholder={
            isAssign
              ? 'Install approved fire collars to all three penetrations and re-inspect.'
              : isDone
                ? 'Collars installed to all three penetrations, photographed and logged.'
                : 'One collar is the wrong diameter for the penetration.'
          }
        />
        <p className="hint">
          {text.trim().length}/{minimum} characters minimum.
        </p>
      </div>

      {isAssign && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="ncr-move-assignee">Who is responsible?</label>
            <select
              id="ncr-move-assignee"
              value={assignee}
              disabled={busy}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="">Choose somebody</option>
              {people.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ncr-move-due">By when?</label>
            <input
              id="ncr-move-due"
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>
      )}

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !ready}
          onClick={() =>
            void onSubmit(
              isAssign
                ? {
                    corrective_action: text.trim(),
                    assignee_id: assignee || null,
                    due_date: due || null,
                  }
                : isDone
                  ? { completion_note: text.trim() }
                  : { reason: text.trim() },
            )
          }
        >
          {busy ? 'Saving…' : (NCR_MOVES[to] ?? 'Save')}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
